/**
 * Auth context — Cloudflare Access (Google IdP) + live D1 role.
 *
 * The SPA sits BEHIND Cloudflare Access, so the browser already holds the
 * Access cookie before any of our code runs. We do NOT manage tokens, do NOT
 * have a login form, and do NOT call any IdP — we just ask the admin-api who we
 * are: `GET /api/me` → { email, role, status }. Identity + role come live from
 * D1 per request (no custom claims, no 1h staleness).
 *
 *   loading  — first /api/me round-trip not yet settled.
 *   email    — verified Access email (null only while unauthenticated/failed).
 *   role     — D1 role (super_admin|admin|support|read_only) or null until approved.
 *   status   — pending|approved|revoked from the admin_users row.
 *   error    — set if /api/me failed (401 = Access cookie missing/expired).
 *
 * signOut = full-page redirect to /cdn-cgi/access/logout (Access handles it).
 * refresh = re-fetch /api/me (used by the Pending page to poll for approval).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, accessLogoutUrl, isApiError } from '@/lib/api';
import type { Me, Role, AdminStatus } from '@/types';

type AuthState = {
  loading: boolean;
  email: string | null;
  role: Role | null;
  status: AdminStatus | null;
  /** signed in via Access AND approved with a role. */
  authorized: boolean;
  error: { code: string; message: string } | null;
  signOut: () => void;
  refresh: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await api.me();
      setMe(next);
      setError(null);
    } catch (e) {
      setMe(null);
      if (isApiError(e)) setError({ code: e.code, message: e.message });
      else setError({ code: 'unknown', message: 'failed to load identity' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    // Access owns the session; clearing it is a full-page redirect.
    window.location.href = accessLogoutUrl();
  }, []);

  const role = me?.role ?? null;
  const status = me?.status ?? null;
  const authorized = !!me && me.status === 'approved' && me.role !== null;

  return (
    <AuthCtx.Provider
      value={{
        loading,
        email: me?.email ?? null,
        role,
        status,
        authorized,
        error,
        signOut,
        refresh,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
